import { AmountText } from '@repo/ui/amount-text';
// import { Button } from '@repo/ui/button';
import { cn } from '@repo/utils/cn';
import { useEffect, useMemo } from 'react';
// import AirdropRedemptionDialog from '@/components/common/airdrop-redemption-dialog';
// import AmountWithHiddenText from '@/components/common/amount-with-hidden-text';
import { ProfileResponseType, WalletResponseType } from '@/lib/fetch';
import { useAppStore } from '@/providers/AppStoreProvider';
import { useTenant } from '@/providers/TenantProvider';

interface BalanceProps {
  balance: WalletResponseType;
  profile: ProfileResponseType | null;
  selectedSecondaryCurrency: string;
}

/**
 * The Balance component displays the user's balance and currency
 * and also sets to the client store of the app the balance, currency and profile status
 */
const Balance = ({ balance, profile, selectedSecondaryCurrency }: BalanceProps) => {
  const { setBalance, hideBalance, setProfileStatus, setCurrency, setProfileUuid, setEmail } = useAppStore((state) => ({
    setBalance: state.setBalance,
    hideBalance: state.hideBalance,
    setProfileStatus: state.setProfileStatus,
    setCurrency: state.setCurrency,
    setProfileUuid: state.setProfileUuid,
    setEmail: state.setEmail,
  }));

  const { primaryBalance, secondaryBalance } = useMemo(
    () => ({
      primaryBalance: balance?.currencies?.find((wallet) => wallet.type === 'PRIMARY'),
      secondaryBalance: balance?.currencies?.find(
        (wallet) => wallet.type === 'SECONDARY' && wallet.currencyCode === selectedSecondaryCurrency
      ),
    }),
    [balance, selectedSecondaryCurrency]
  );

  const { tenantConfig } = useTenant();

  useEffect(() => {
    if (primaryBalance?.playableBalance != null) {
      setBalance(primaryBalance.playableBalance);
    }
    if (primaryBalance?.currencyCode) {
      setCurrency(primaryBalance.currencyCode);
    }

    return () => {
      setBalance(null);
      setCurrency(null);
    };
  }, [primaryBalance]);

  useEffect(() => {
    if (profile?.status) {
      setProfileStatus(profile.status);
    }
    if (profile?.uuid) {
      setProfileUuid(profile.uuid);
    }

    return () => {
      setProfileStatus(null);
      setProfileUuid(null);
    };
  }, [profile?.status, profile?.uuid, profile?.avatar]);

  useEffect(() => {
    if (profile?.email) {
      setEmail(profile.email);
    }
  }, [profile?.email]);

  // const handleClick = () => {
  //   toggleBalanceDisplay!();
  // };

  // TODO: Disable airdrop redemption dialog
  // const airdropInfo = useMemo(() => {
  //   if (!profile?.airdropRedeemed) {
  //     return null;
  //   }

  //   const redeemedAllocation = profile?.airdropInfo?.allocations?.find(
  //     (allocation) => allocation.status === 'REDEEMED'
  //   );
  //   return {
  //     vatanAmount: redeemedAllocation?.vatanCoins ?? 0,
  //     usdAmount: Number(((redeemedAllocation?.vatanCoins ?? 0) * (redeemedAllocation?.exchangeRate ?? 0)).toFixed(2)),
  //   };
  // }, [profile?.airdropInfo?.allocations, profile?.airdropRedeemed]);

  return (
    <article
      className="flex h-full min-w-20 flex-shrink-0 flex-grow flex-row items-center justify-center"
      data-testid="header-balance"
    >
      <div
        className="items-center justify-center leading-4 rtl:leading-3"
        data-testid={hideBalance ? 'header-balance-total-hidden' : 'header-balance-total'}
      >
        <AmountText
          currency={primaryBalance?.currencyCode!}
          value={primaryBalance?.playableBalance!}
          className={cn(
            'flex w-full justify-start vt-font-weight-bold vt-utility-white vt-text-size-medium',
            !tenantConfig?.secondaryCurrency && 'vt-text-size-big sm:vt-text-size-big-sm'
          )}
        />
        {tenantConfig?.secondaryCurrency && (
          <div className="flex w-full justify-end">
            <AmountText
              currency={secondaryBalance?.currencyCode!}
              value={secondaryBalance?.playableBalance!}
              abbreviateAmount
              className={cn(
                'max-w-[110px] truncate vt-text-size-small vt-body-text-dark sm:vt-text-size-small-sm',
                hideBalance && '!overflow-visible'
              )}
            />
          </div>
        )}
      </div>
      {/* <Button
        size="sm"
        variant="grey"
        className="px-2"
        data-testid="header-balance-eye-icon"
        onClick={handleClick}
      ></Button> */}
      {/* {airdropInfo && (
        <AirdropRedemptionDialog
          open
          vatanAmount={airdropInfo.vatanAmount}
          usdAmount={airdropInfo.usdAmount}
        />
      )} */}
    </article>
  );
};

export default Balance;
